import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'motion/react';
import { Link } from 'react-router-dom';
import {
  Droplets,
  ArrowRight,
  CheckCircle,
  Waves,
  TrendingUp,
  Zap,
  Users,
  BarChart3,
  Clock,
  Shield,
  Target,
  Eye,
  Play,
  Award,
  Wifi,
  Battery,
  Bell,
  MapPin,
  Cloud
} from 'lucide-react';
import { ImageWithFallback } from '../../figma/ImageWithFallback';
import { ProductTestimonials } from '../../shared/ProductTestimonials';
import { waterLevelIoTTestimonials } from '../../shared/testimonialData';

const WaterLevelIoT: React.FC = () => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: containerRef });
  const backgroundY = useTransform(scrollYProgress, [0, 1], ['0%', '50%']);


  const features = [
    {
      icon: Waves,
      title: '	Automatic Level Sensing ',
      description: 'Continuously monitors the water level using simple, robust sensors placed at key heights inside the tank.'
    },
    {
      icon: Bell,
      title: '	Pump Auto ON / OFF Control ',
      description: 'Starts the motor automatically when the water drops below the minimum level and stops it when the tank reaches the preset full level.'
    },
    {
      icon: BarChart3,
      title: '	Visual Level Indication ',
      description: 'LED or display indications show whether the tank is near empty, half full, or full, so users can check status at a glance.'
    },
    {
      icon: Cloud,
      title: 'Support for Multi-Storey Buildings ',
      description: 'Designed to operate reliably for tanks installed on buildings up to three to four floors high from ground level.'
    },
    {
      icon: Battery,
      title: 'Compact, Low-Maintenance Circuit ',
      description: 'Uses a simple, rugged electronic design with minimal moving parts, keeping maintenance and replacement costs low.'
    },
    {
      icon: MapPin,
      title: 'Multi-location Management',
      description: 'Centralized monitoring of multiple water sources, tanks, and reservoirs from a single dashboard.'
    }
  ];

  const solutions = [
    {
      icon: Droplets,
      title: '	Residential Buildings and Villas',
      description: 'Eliminates the need for residents to manually monitor and switch the water pump. Early-morning and late-night pumping can be handled automatically, avoiding overflow on terraces and in balconies.',
    },
    {
      icon: Target,
      title: '	Apartments, Hostels and PG Facilities',
      description: 'Maintenance staff can rely on the controller to keep tanks replenished even when occupancy changes. Level indication helps them quickly confirm that all blocks have enough water during peak-usage hours.',
    },
    {
      icon: Shield,
      title: 'Small Industries and Workshops',
      description: 'Units that depend on water for cooling, cleaning or process steps benefit from stable tank levels. The controller ensures that operations do not stop because of a dry tank and reduces the risk of overflow near machinery.',
    },
    {
      icon: Users,
      title: 'Schools, Clinics and Offices',
      description: 'Facilities teams can standardize water management across multiple tanks, ensuring hygiene and availability without dedicating manpower to watch the motor switch.',
    },
    {
      icon: Zap,
      title: '	Farmhouses and Rural Installations',
      description: 'Where electricity supply is patchy, the controller ensures that whenever power is available, water is pumped efficiently without overflow, making the most of each power window.',
    }
  ];

  const benefits = [
    {
      icon: TrendingUp,
      title: '	Water Conservation',
      description: 'By preventing overflow and reducing unnecessary pumping, the system directly cuts wastage  a visible step toward responsible water use in homes, societies and businesses.',
    },
    {
      icon: Clock,
      title: '	Energy and Cost Savings',
      description: 'Motors no longer run longer than needed or run dry, which reduces electricity bills and extends pump life. Over a year, this can translate into meaningful savings for apartments and commercial properties.',
    },
    {
      icon: Zap,
      title: '	Reduced Manual Effort',
      description: 'Residents and facility teams don’t have to keep an eye on rooftop tanks or stand next to switches waiting for the “right moment.” The controller handles the routine work, freeing people to focus on higher-value tasks.',
    },
    {
      icon: Award,
      title: '	Improved Reliability of Supply',
      description: 'The automatic ON feature ensures that tanks are replenished before they become critically low, so water is available when occupants need it, even during high-demand periods.',
    },
    {
      icon: CheckCircle,
      title: '	Cleaner, Safer Premises',
      description: 'No overflow means fewer damp patches, less moss or algae growth, and reduced risk of slips and structural damage around the building — all of which matter in shared and commercial spaces.',
    }
  ];

  const compliances = [
    {
      icon: Shield,
      title: 'Electrical Safety and Isolation',
      description: 'The control circuit is designed with proper isolation between the low-voltage sensing electronics and the higher-voltage pump line, reducing risk of electrical shock and equipment damage.',
    },
    {
      icon: Cloud,
      title: 'Fail-Safe Design Principles',
      description: 'In case of sensor failure or wiring issues, the default design can be configured to stop the pump, protecting the motor from dry running and the property from overflow.',
    },
    {
      icon: CheckCircle,
      title: 'Compliance with Local Wiring Standards',
      description: 'The system can be installed in line with common electrical codes and best practices for domestic and light-commercial buildings, helping owners stay aligned with safety guidelines.',
    },
    {
      icon: BarChart3,
      title: '	Protected Housing and Cabling',
      description: 'Weather-resistant enclosures and shielded cabling help guard the electronics against moisture and accidental tampering, ensuring reliable, compliant operation over time.',
    },
    {
      icon: Users,
      title: '	Optional Smart Add-Ons',
      description: 'Where customers choose to connect the controller to a building automation system or gateway, communication can be routed through secure, encrypted channels and restricted networks, keeping usage and status data protected.',
    }
  ];

  const deploymentProcess = [
    {
      step: '01',
      title: 'Site Assessment',
      description: 'Evaluate water sources, connectivity requirements, and optimal sensor placement for maximum accuracy.'
    },
    {
      step: '02',
      title: 'System Installation',
      description: 'Professional installation of sensors, communication devices, and power systems with minimal disruption.'
    },
    {
      step: '03',
      title: 'Configuration & Testing',
      description: 'System calibration, alert setup, dashboard configuration, and comprehensive testing procedures.'
    },
    {
      step: '04',
      title: 'Training & Support',
      description: 'User training, documentation, and ongoing technical support to ensure optimal system performance.'
    }
  ];

  return (
    <div ref={containerRef} className="relative min-h-screen pt-20">
      {/* Animated Background */}
      <motion.div
        style={{ y: backgroundY }}
        className="fixed inset-0 opacity-20"
      >
        <div className="absolute inset-0">
          {Array.from({ length: 6 }).map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-8 h-8 bg-blue-500 rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                scale: [0, 1, 0],
                opacity: [0, 0.8, 0],
              }}
              transition={{
                duration: 8,
                delay: i * 0.8,
                repeat: Infinity,
                repeatType: "loop"
              }}
            />
          ))}
        </div>
      </motion.div>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: -100 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="flex items-center gap-3 mb-6"
              >
                <div className="p-3 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-600 neon-glow">
                  <Droplets className="w-8 h-8 text-white" />
                </div>
                <div>
                  <span className="text-sm font-medium text-blue-400 uppercase tracking-wide">Smart Water Management</span>
                  <h1 className="text-4xl lg:text-6xl font-bold">
                    Water Level <span className="bg-gradient-to-r from-blue-500 to-cyan-600 bg-clip-text text-transparent">IoT System</span>
                  </h1>
                </div>
              </motion.div>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="text-xl text-muted-foreground mb-8 leading-relaxed"
              >
                Most homes, apartment blocks, and small commercial buildings rely on overhead water tanks. Yet the way these tanks are managed is still surprisingly manual. People switch the pump on and then guess when the tank is full. Sometimes the pump is turned off too early and there’s not enough water for the day sometimes it’s forgotten, the tank overflows, and precious water  and electricity  are wasted. For businesses and housing societies, this turns an uncontrolled, leak-prone process into a clean, trackable utility function that saves water, reduces power costs, and takes one more manual task off the maintenance team’s plate.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                className="flex flex-col sm:flex-row gap-4 mb-12"
              >
                <Link
                  to="/contact"
                  className="group inline-flex items-center justify-center gap-3 bg-gradient-to-r from-blue-500 to-cyan-600 px-8 py-4 rounded-xl text-white font-semibold hover:shadow-2xl hover:shadow-blue-500/25 transition-all duration-300"
                >
                  Deploy Smart Monitoring
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                <button className="inline-flex items-center justify-center gap-3 glass px-8 py-4 rounded-xl text-white font-semibold hover:glass-strong transition-all duration-300">
                  <Play className="w-5 h-5" />
                  System Demo
                </button>
              </motion.div>

              {/* Key Metrics */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.8 }}
                className="grid grid-cols-3 gap-8 hidden"
              >
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-500">1000+</div>
                  <div className="text-sm text-muted-foreground">Sensors Deployed</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-cyan-400">99.9%</div>
                  <div className="text-sm text-muted-foreground">Uptime</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-accent">5yr</div>
                  <div className="text-sm text-muted-foreground">Battery Life</div>
                </div>
              </motion.div>
            </motion.div>

            {/* Hero Image */}
            <motion.div
              initial={{ opacity: 0, x: 100, rotateY: -15 }}
              animate={{ opacity: 1, x: 0, rotateY: 0 }}
              transition={{ duration: 1, delay: 0.3 }}
              className="relative perspective-1000"
            >
              <div className="relative group">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 45, repeat: Infinity, ease: "linear" }}
                  className="absolute -inset-10 bg-gradient-to-r from-blue-500 to-cyan-600 opacity-20 rounded-full blur-3xl"
                />

                <motion.div
                  whileHover={{ scale: 1.05, rotateY: 5, rotateX: 5 }}
                  transition={{ duration: 0.4 }}
                  className="relative z-10 glass-strong rounded-2xl overflow-hidden transform-gpu"
                >
                  <ImageWithFallback
                    src='/assets/Products/iot-Products/waterLevelIoT.png'
                    alt="Water Level IoT Monitoring System"
                    className="w-full h-96 object-cover"
                  />

                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent">
                    <div className="absolute bottom-6 left-6 right-6">
                      <div className="flex items-center gap-3 text-white">
                        <div className="p-2 bg-blue-500/20 rounded-lg backdrop-blur-sm">
                          <Droplets className="w-5 h-5 text-blue-500" />
                        </div>
                        <div>
                          <p className="font-semibold">IoT-Powered</p>
                          <p className="text-sm text-white/80">Water Monitoring</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: 1 }}
                  className="absolute -top-4 -right-4 glass p-3 rounded-xl"
                >
                  <div className="flex items-center gap-2 text-blue-500">
                    <Wifi className="w-4 h-4 animate-pulse" />
                    <span className="text-xs font-mono">CONNECTED</span>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Deployment Process Section */}
      <section className="relative py-15 hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Simple Deployment Process
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto text-center">
              From assessment to full operation, our streamlined deployment process ensures your water monitoring system is up and running quickly.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {deploymentProcess.map((process, index) => (
              <motion.div
                key={process.step}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                viewport={{ once: true }}
                className="relative"
              >
                <div className="glass p-6 rounded-xl hover:glass-strong transition-all duration-300">
                  <div className="text-3xl font-bold text-blue-500 mb-4">{process.step}</div>
                  <h3 className="text-lg font-semibold mb-3">{process.title}</h3>
                  <p className="text-muted-foreground text-sm">{process.description}</p>
                </div>
                {index < deploymentProcess.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 w-8 h-0.5 bg-gradient-to-r from-blue-500 to-cyan-600"></div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="relative py-15">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Core <span className="bg-gradient-to-r from-blue-500 to-cyan-600 bg-clip-text text-transparent">Features</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto text-center">
              Comprehensive water level monitoring with cutting-edge sensor technology and intelligent analytics for optimal water management.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -5 }}
                  className="glass p-6 rounded-xl hover:glass-strong transition-all duration-300"
                >
                  <div className="mb-4">
                    <Icon className="w-8 h-8 text-blue-500" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">{feature.title}</h3>
                  <p className="text-muted-foreground">{feature.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>


      {/* solutions Section */}
      <section className="relative py-15">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Solutions
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-8">
            {solutions.map((solution, index) => {
              const Icon = solution.icon;
              return (
                <motion.div
                  key={solution.title}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -5 }}
                  className="glass p-6 rounded-xl hover:glass-strong transition-all duration-300"
                >
                  <div className="mb-4">
                    <Icon className="w-8 h-8 text-blue-500" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">{solution.title}</h3>
                  <p className="text-muted-foreground">{solution.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>


      {/* compliances Specifications */}
      <section className="relative py-15">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Data Security & <span className="bg-gradient-to-r from-blue-500 to-cyan-600 bg-clip-text text-transparent">Compliance</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {compliances.map((compliance, index) => {
              const Icon = compliance.icon;
              return (
                <motion.div
                  key={compliance.title}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -5 }}
                  className="glass p-6 rounded-xl hover:glass-strong transition-all duration-300"
                >
                  <div className="mb-4">
                    <Icon className="w-8 h-8 text-blue-500" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">{compliance.title}</h3>
                  <p className="text-muted-foreground">{compliance.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="relative py-15">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
             Platform <span className="bg-gradient-to-r from-blue-500 to-cyan-600 bg-clip-text text-transparent">Benefits</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto text-center">
              Transform your water management operations with quantifiable improvements in conservation, efficiency, and system reliability.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <motion.div
                  key={benefit.title}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -5 }}
                  className="glass p-6 rounded-xl hover:glass-strong transition-all duration-300"
                >
                  <div className="mb-4">
                    <Icon className="w-8 h-8 text-blue-500" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">{benefit.title}</h3>
                  <p className="text-muted-foreground">{benefit.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="relative py-24 hidden">
        <ProductTestimonials
          testimonials={waterLevelIoTTestimonials}
          title="Water Management Success"
          subtitle="See how organizations transformed water monitoring with IoT technology"
          gradientFrom="#00CFFF"
          gradientVia="#0EA5E9"
          gradientTo="#00CFFF"
          iconColor="#00CFFF"
          dotColor="bg-blue-500"
          speed={60}
          direction="left"
        />
      </section>

      {/* CTA Section */}
      <section className="relative py-15">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Deploy Smart Water Monitoring Today
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Transform your water management with IoT technology. Monitor, analyze, and optimize water resources with real-time intelligence.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="group inline-flex items-center justify-center gap-3 bg-gradient-to-r from-blue-500 to-cyan-600 px-8 py-4 rounded-xl text-white font-semibold hover:shadow-2xl hover:shadow-blue-500/25 transition-all duration-300"
              >
                Deploy Smart Monitoring
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-3 glass px-8 py-4 rounded-xl text-white font-semibold hover:glass-strong transition-all duration-300"
              >
                Request Quote
                <Eye className="w-5 h-5" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export { WaterLevelIoT };